import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  CircularProgress,
  Chip,
  Button,
  Grid,
  Card,
  CardContent,
  Divider,
  Stack
} from '@mui/material';
import { Receipt, CreditCard, LocationOn, OpenInNew } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import CustomerLayout from '../../components/customer/CustomerLayout';
import customerApiService from '../../services/customerApi';
import { EndUserTransaction } from '../../types/endUser';

const CustomerBillingPage: React.FC = () => {
  const { t } = useTranslation();
  const [transactions, setTransactions] = useState<EndUserTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadTransactions();
  }, []);

  const loadTransactions = async () => {
    try {
      console.log('Loading transactions...');
      const data = await customerApiService.getTransactions(50);
      console.log('Transactions loaded:', data);
      setTransactions(data.transactions || []);
      setError(null);
    } catch (err: any) {
      console.error('Failed to load transactions:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to load transactions');
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <CustomerLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </CustomerLayout>
    );
  }

  return (
    <CustomerLayout>
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, mb: 4 }}>
          {t('customer.billing.title')}
        </Typography>

        {error && (
          <Paper elevation={0} sx={{ border: '1px solid #f44336', borderRadius: 2, p: 3, mb: 3, bgcolor: '#ffebee' }}>
            <Typography color="error" fontWeight={600}>
              Error: {error}
            </Typography>
          </Paper>
        )}

        {transactions.length === 0 ? (
          <Paper elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              {t('customer.billing.noTransactions')}
            </Typography>
          </Paper>
        ) : (
          <Grid container spacing={3}>
            {transactions.map((txn) => (
              <Grid item xs={12} key={txn.id}>
                <Card elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}>
                  <CardContent sx={{ p: 3 }}>
                    <Grid container spacing={3}>
                      {/* Transaction Header */}
                      <Grid item xs={12}>
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                          <Box>
                            <Typography variant="h6" fontWeight={600} gutterBottom>
                              {txn.products && txn.products.length > 0 ? txn.products[0].name : 'Transacción'}
                            </Typography>
                            
                            {/* Subscription Info (Traceability) */}
                            {txn.subscription_name && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <Chip 
                                  label={txn.subscription_name}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                  sx={{ fontWeight: 600 }}
                                />
                                {txn.subscription_status && (
                                  <Chip 
                                    label={txn.subscription_status}
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontSize: '0.7rem' }}
                                  />
                                )}
                              </Box>
                            )}
                            
                            <Typography variant="caption" color="text.secondary" fontFamily="monospace" display="block">
                              Transaction ID: {txn.paddle_transaction_id}
                            </Typography>
                            {txn.paddle_subscription_id && (
                              <Typography variant="caption" color="text.secondary" fontFamily="monospace" display="block">
                                Subscription ID: {txn.paddle_subscription_id}
                              </Typography>
                            )}
                          </Box>
                          <Box textAlign="right">
                            <Typography variant="h5" fontWeight={700} color="primary.main">
                              €{txn.amount_total?.toFixed(2) || '0.00'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              (inc impuestos)
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>

                      <Grid item xs={12}>
                        <Divider />
                      </Grid>

                      {/* Transaction Details */}
                      <Grid item xs={12} md={4}>
                        <Stack spacing={2}>
                          <Box>
                            <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                              Estado
                            </Typography>
                            <Chip
                              label={txn.status === 'completed' ? 'Completado' : txn.status}
                              size="small"
                              color={txn.status === 'completed' ? 'success' : 'default'}
                            />
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                              Fecha
                            </Typography>
                            <Typography variant="body2" fontWeight={600}>
                              {(() => {
                                const dateStr = txn.paddle_created_at || txn.created_at;
                                // Add 'Z' if not present to indicate UTC
                                const utcDate = dateStr.endsWith('Z') ? dateStr : `${dateStr}Z`;
                                return new Date(utcDate).toLocaleString(i18n.language === 'es' ? 'es-ES' : 'en-US', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                });
                              })()}
                            </Typography>
                          </Box>
                        </Stack>
                      </Grid>

                      {/* Payment Method */}
                      {txn.payment_card_type && txn.payment_card_last4 && (
                        <Grid item xs={12} md={4}>
                          <Box>
                            <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                              <CreditCard sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                              Método de Pago
                            </Typography>
                            <Typography variant="body2" fontWeight={600} textTransform="capitalize">
                              {txn.payment_card_type} •••• {txn.payment_card_last4}
                            </Typography>
                            {txn.payment_card_expiry_month && txn.payment_card_expiry_year && (
                              <Typography variant="caption" color="text.secondary">
                                exp. {txn.payment_card_expiry_month}/{txn.payment_card_expiry_year}
                              </Typography>
                            )}
                          </Box>
                        </Grid>
                      )}

                      {/* Billing Address */}
                      {(txn.billing_postal_code || txn.billing_country_code) && (
                        <Grid item xs={12} md={4}>
                          <Box>
                            <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                              <LocationOn sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                              Dirección
                            </Typography>
                            <Typography variant="body2" fontWeight={600}>
                              {txn.billing_postal_code && <>{txn.billing_postal_code}</>}
                              {txn.billing_postal_code && txn.billing_country_code && <>, </>}
                              {txn.billing_country_code && <>{txn.billing_country_code.toUpperCase()}</>}
                            </Typography>
                          </Box>
                        </Grid>
                      )}

                      {/* Products */}
                      {txn.products && txn.products.length > 0 && (
                        <Grid item xs={12}>
                          <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1 }}>
                            <Typography variant="caption" color="text.secondary" gutterBottom display="block" fontWeight={600}>
                              Productos
                            </Typography>
                            {txn.products.map((product, idx) => (
                              <Box key={idx} display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                <Typography variant="body2">
                                  {product.name} {product.quantity && product.quantity > 1 && `x${product.quantity}`}
                                </Typography>
                                <Typography variant="body2" fontWeight={600}>
                                  €{product.price_amount?.toFixed(2) || '0.00'}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        </Grid>
                      )}

                      {/* Pricing Breakdown */}
                      <Grid item xs={12}>
                        <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1 }}>
                          <Typography variant="caption" color="text.secondary" gutterBottom display="block" fontWeight={600}>
                            Desglose de Precio
                          </Typography>
                          <Box display="flex" justifyContent="space-between" mb={0.5}>
                            <Typography variant="body2" color="text.secondary">Subtotal (exc impuestos)</Typography>
                            <Typography variant="body2" fontWeight={600}>€{txn.amount_subtotal?.toFixed(2) || '0.00'}</Typography>
                          </Box>
                          {txn.amount_tax && txn.amount_tax > 0 && (
                            <Box display="flex" justifyContent="space-between" mb={0.5}>
                              <Typography variant="body2" color="text.secondary">
                                Impuestos {txn.amount_subtotal && txn.amount_subtotal > 0 
                                  ? `(${((txn.amount_tax / txn.amount_subtotal) * 100).toFixed(0)}%)`
                                  : ''}
                              </Typography>
                              <Typography variant="body2" fontWeight={600}>€{txn.amount_tax.toFixed(2)}</Typography>
                            </Box>
                          )}
                          <Divider sx={{ my: 1 }} />
                          <Box display="flex" justifyContent="space-between">
                            <Typography variant="body1" fontWeight={700}>Total</Typography>
                            <Typography variant="body1" fontWeight={700} color="primary.main">
                              €{txn.amount_total?.toFixed(2) || '0.00'} (inc impuestos)
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>

                      {/* Billing Period (for subscriptions) */}
                      {txn.billing_period_start && txn.billing_period_end && (
                        <Grid item xs={12}>
                          <Box>
                            <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                              Período de Facturación
                            </Typography>
                            <Typography variant="body2" fontWeight={600}>
                              {new Date(txn.billing_period_start).toLocaleDateString()} - {new Date(txn.billing_period_end).toLocaleDateString()}
                            </Typography>
                          </Box>
                        </Grid>
                      )}

                      {/* Customer Info */}
                      {(txn.billing_email || txn.billing_name) && (
                        <Grid item xs={12}>
                          <Box>
                            <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                              Cliente
                            </Typography>
                            {txn.billing_name && (
                              <Typography variant="body2" fontWeight={600}>
                                {txn.billing_name}
                              </Typography>
                            )}
                            {txn.billing_email && (
                              <Typography variant="body2" color="text.secondary">
                                {txn.billing_email}
                              </Typography>
                            )}
                          </Box>
                        </Grid>
                      )}

                      {/* Actions */}
                      {(txn.receipt_url || txn.invoice_url) && (
                        <Grid item xs={12}>
                          <Stack direction="row" spacing={2}>
                            {txn.receipt_url && (
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<Receipt />}
                                endIcon={<OpenInNew />}
                                href={txn.receipt_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ textTransform: 'none', borderRadius: 2 }}
                              >
                                Ver Recibo
                              </Button>
                            )}
                            {txn.invoice_url && (
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<Receipt />}
                                endIcon={<OpenInNew />}
                                href={txn.invoice_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ textTransform: 'none', borderRadius: 2 }}
                              >
                                Ver Factura
                              </Button>
                            )}
                          </Stack>
                        </Grid>
                      )}
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>
    </CustomerLayout>
  );
};

export default CustomerBillingPage;
